import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Switch,
  Checkbox as CheckboxChakra,
  HStack,
  Button,
  Box,
  ModalFooter,
  useToast,
  Flex,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import context from '../context';
import Datepicker from '../../Form/Datepicker';
import Input from '../../Form/Input';
import {
  CREATE_PAGE_GROUP,
  REMOVE_PAGE_GROUP,
  UPDATE_PAGE_GROUP,
} from '../../../graphQL/mutations';
import { GET_PAGE_DATA } from '../../../graphQL/queries';
import Textarea from '../../Form/Textarea';
import sanitizeString from '../../../utils/sanitizeString';
import Select from '../../Form/Select';
import Checkbox from '../../Form/Checkbox';
import sizeTypes from '../../../utils/sizeType';
import carouselBannerTypes from '../../../utils/carouselBannerTypes';

interface Props {
  onCreated?: (data: any) => void;
}
interface FormData {
  name: string;
  description?: string;
  height?: string;
  width?: string;
  countdown: boolean;
  schedule: boolean;
  schedule_start?: Date;
  schedule_end?: Date;
  padding: number;
  is_full_width_banner: boolean;
  round_shape?: boolean;
  size: {
    label: string | string;
    value: string | string;
  };
  type: {
    label: string | string;
    value: string | string;
  };
  sincronizarSF?: boolean;
}

const formData = yup.object().shape({
  name: yup.string().nullable(),
  description: yup.string().nullable(),
  height: yup.string().nullable(),
  width: yup.string().nullable(),
  schedule: yup.boolean(),
  round_shape: yup.boolean(),
  countdown: yup.boolean(),
  is_full_width_banner: yup.boolean(),
  sincronizarSF: yup.boolean(),
  size: yup.mixed().when('is_full_width_banner', {
    is: true,
    then: yup.mixed().optional(),
    otherwise: yup.object().shape({
      value: yup.string().required('Tamanho é obrigatório'),
      label: yup.string().required('Tamanho é obrigatório'),
    }),
  }),
  type: yup.mixed().when('is_full_width_banner', {
    is: true,
    then: yup.mixed().optional(),
    otherwise: yup.object().shape({
      value: yup.string().required('Tipo é obrigatório'),
      label: yup.string().required('Tipo é obrigatório'),
    }),
  }),
  schedule_start: yup
    .mixed()
    .when('schedule', {
      is: true,
      then: yup.date().required('Data de início é obrigatória'),
    })
    .optional(),
  schedule_end: yup
    .mixed()
    .when(['schedule', 'schedule_start'], (schedule, schedule_start) => {
      if (!schedule) {
        return yup.date();
      }
      if (schedule_start) {
        return yup
          .date()
          .min(schedule_start, 'Data final deve ser superior a data inicial')
          .required('Data Final é obrigatória');
      }
      return yup.date().required('Data Final é obrigatória');
    }),
});

const Form: React.FC<Props> = ({ onCreated }) => {
  const toast = useToast();

  const { pageGroup, newPageGroupDTO, onPageGroupCreated, onClose, display } =
    React.useContext(context);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver: yupResolver(formData),
  });

  const [createPageGroup, { loading: createLoading, error: createError }] =
    useMutation(CREATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [updatePageGroup, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [removePageGroup, { loading: deleteLoading, error: deleteError }] =
    useMutation(REMOVE_PAGE_GROUP, {
      refetchQueries: [GET_PAGE_DATA, 'GetPageData'],
    });

  const [schedule, setSchedule] = useState(!!pageGroup?.schedule_end);
  const [fullWidth, setFullWidth] = useState(false);

  const { errors } = formState;

  useEffect(() => {
    if (createError || updateError || deleteError) {
      toast({
        title: 'Opss!!',
        description:
          createError?.message ||
          updateError?.message ||
          deleteError?.message ||
          'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, updateError, deleteError]);

  useEffect(() => {
    if (!pageGroup) return;
    setValue('padding', pageGroup.padding);
    if (pageGroup?.schedule_end) {
      setValue('schedule', true);
      setValue('schedule_start', new Date(pageGroup.schedule_start));
      setValue('schedule_end', new Date(pageGroup.schedule_end));
      setValue('countdown', pageGroup.countdown);
    }
    setValue('description', pageGroup?.description);
    setValue('height', pageGroup?.height);
    setValue('width', pageGroup?.width);
    setValue('name', pageGroup.name);
    if (pageGroup?.is_full_width_banner) {
      setValue('is_full_width_banner', true);
      setFullWidth(true);
    }

    if (pageGroup?.round_shape) {
      setValue('round_shape', true);
    }

    setValue(
      'type',
      carouselBannerTypes.find(type => type?.value === pageGroup?.type),
    );
    setValue(
      'size',
      sizeTypes.find(size => size?.value === pageGroup?.size),
    );
    // usa redirect_type do grupo como flag de sincronização SF
    setValue('sincronizarSF', pageGroup?.redirect_type === 'sync_sf');
  }, [pageGroup, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      const sanitizedObj = sanitizeString(data);

      const { schedule: scheduleData, sincronizarSF, ...rest } = sanitizedObj;
      const syncFlag = !!sincronizarSF;

      if (pageGroup) {
        await updatePageGroup({
          variables: {
            updatePageGroupInput: {
              ...rest,
              schedule_end: scheduleData ? rest.schedule_end : null,
              padding: Number(data?.padding),
              countdown: scheduleData ? rest.countdown : false,
              id: pageGroup?.id,
              size: data.size?.value,
              is_full_width_banner: !!data.is_full_width_banner,
              round_shape: !!data.round_shape,
              type: data.type?.value,
              redirect_type: syncFlag ? 'sync_sf' : null,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        toast({
          title: 'Sucesso!',
          description: 'Grupo editado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      } else {
        const apiData = await createPageGroup({
          variables: {
            createPageGroupInput: {
              ...rest,
              ...newPageGroupDTO,
              element_display_id: display?.id,
              padding: Number(data?.padding),
              size: data.size?.value,
              is_full_width_banner: !!data.is_full_width_banner,
              round_shape: !!data.round_shape,
              type: data.type?.value,
              redirect_type: syncFlag ? 'sync_sf' : null,
              schedule_start: scheduleData ? rest.schedule_start : new Date(),
            },
          },
        });
        if (onCreated) onCreated(apiData);
        if (onPageGroupCreated) onPageGroupCreated(apiData);
        toast({
          title: 'Sucesso!',
          description: 'Grupo criado com sucesso!!',
          status: 'success',
          position: 'top-right',
        });
      }
    } catch {
      // nada
    }
  };

  const handleDelete = async () => {
    try {
      const confirmed = window.confirm(
        'Tem certeza que deseja excluir o grupo?',
      );
      if (confirmed) {
        await removePageGroup({
          variables: {
            id: pageGroup?.id,
          },
        });
        toast({
          title: 'Sucess!!',
          description: 'Grupo deletado com sucesso',
          status: 'success',
          position: 'top-right',
        });
        if (onClose) onClose();
      }
    } catch {
      // error
    }
  };

  return (
    <Box>
      <form onSubmit={(handleSubmit as any)(handleForm)}>
        <VStack spacing="8" minHeight="fit-content">
          <SimpleGrid
            minChildWidth="200px"
            spacing="4"
            w="100%"
            minHeight="fit-content"
          >
            <Input label="Título" error={errors?.name} {...register('name')} />
            <Input
              label="Largura"
              placeholder="Ex: 120px"
              type="number"
              isDisabled={fullWidth}
              error={errors?.width}
              {...register('width')}
            />
            <Input
              label="Altura"
              placeholder="Ex: 150px"
              type="number"
              error={errors?.height}
              {...register('height')}
            />
            <Input
              label="Padding (px)"
              type="number"
              isRequired
              error={errors?.padding}
              {...register('padding')}
            />
          </SimpleGrid>
          <Textarea
            label="Subtítulo"
            error={errors?.description}
            {...register('description')}
          />
          <Select
            name="size"
            control={control}
            label="Tamanho"
            width="150px"
            error={errors.size}
            isRequired={!fullWidth}
            isDisabled={fullWidth}
            options={sizeTypes}
            onChange={() => {
              setValue('size', '');
            }}
          />
          <Select
            name="type"
            width="150px"
            isRequired={!fullWidth}
            isDisabled={fullWidth}
            control={control}
            label="Tipo"
            error={errors.type}
            options={carouselBannerTypes}
            onChange={() => {
              setValue('type', '');
            }}
          />
          <Flex width="100%" justifyContent="left">
            <Checkbox
              control={control}
              label="Formato redondo"
              {...register('round_shape')}
            />
          </Flex>
          <Flex width="100%" justifyContent="left">
            <CheckboxChakra
              {...register('is_full_width_banner')}
              isChecked={fullWidth}
              onChange={e => {
                setFullWidth(e?.target?.checked);
                if (e.target.checked) {
                  setValue('size', '');
                  setValue('type', '');
                  setValue('width', '');
                }
              }}
            >
              Largura total
            </CheckboxChakra>
          </Flex>
          <Flex width="100%" justifyContent="left">
            <CheckboxChakra colorScheme="brand" {...register('sincronizarSF')}>
              Sincronizar SalesForce
            </CheckboxChakra>
          </Flex>
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="schedule" mb="0">
              Agendamento
            </FormLabel>
            <Switch
              id="schedule"
              size="lg"
              colorScheme="brand"
              isChecked={schedule}
              {...register('schedule')}
              onChange={() => setSchedule(state => !state)}
            />
          </FormControl>
          {schedule && (
            <>
              <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
                <Datepicker
                  isRequired
                  control={control}
                  {...register('schedule_start')}
                  label="Início"
                  showTimeSelect
                  error={errors?.schedule_start}
                />
                <Datepicker
                  isRequired
                  control={control}
                  {...register('schedule_end')}
                  label="Final"
                  showTimeSelect
                  error={errors?.schedule_end}
                />
              </SimpleGrid>
              <CheckboxChakra
                alignSelf="flex-start"
                colorScheme="brand"
                {...register('countdown')}
              >
                Contagem Regressiva
              </CheckboxChakra>
            </>
          )}
        </VStack>
      </form>
      <ModalFooter>
        <HStack spacing={4}>
          {!!pageGroup && (
            <Button
              onClick={handleDelete}
              colorScheme="red"
              isLoading={deleteLoading}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            colorScheme="brand"
            isLoading={createLoading || updateLoading}
            onClick={(handleSubmit as any)(handleForm)}
          >
            Salvar
          </Button>
        </HStack>
      </ModalFooter>
    </Box>
  );
};

export default Form;
